import { Component } from '@theme/component';

const DISMISSED_KEY = 'cart-order-bumps-dismissed';

/**
 * Cart re-renders morph this element's children against server HTML, so any node
 * reference or listener attached to a child dies on the first cart update. Navigation
 * therefore uses the theme's declarative `on:click` bindings (document-level delegation)
 * and every DOM lookup happens lazily at call time.
 *
 * @extends {Component}
 */
class OrderBumpsCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();

    // The morph also strips `hidden` back off (server HTML never includes it),
    // so re-apply the dismissed state immediately.
    this._observer ??= new MutationObserver(() => {
      if (sessionStorage.getItem(DISMISSED_KEY) === 'true' && !this.hidden) {
        this.hidden = true;
      }
    });
    this._observer.observe(this, { attributes: true, attributeFilter: ['hidden'] });

    if (sessionStorage.getItem(DISMISSED_KEY) === 'true') {
      this.hidden = true;
      return;
    }

    this._controller?.abort();
    this._controller = new AbortController();
    const signal = this._controller.signal;

    if (this._count <= 1) return;

    if (this._autoScroll) {
      this._start();
      this.addEventListener('pointerenter', () => this._stop(), { signal });
      this.addEventListener('pointerleave', () => this._start(), { signal });
    }
  }

  get _count() {
    return parseInt(this.dataset.count || '1', 10);
  }

  get _autoScroll() {
    return this.dataset.autoScroll === 'true';
  }

  get _delay() {
    return parseInt(this.dataset.autoScrollInterval || '4000', 10);
  }

  /**
   * Current slide is read from the DOM instead of instance state: after a morph the
   * track transform and dots reset to the first slide while any stored index would not.
   */
  get _index() {
    const dots = [...this.querySelectorAll('.order-bumps__dot')];
    const current = dots.findIndex((dot) => dot.getAttribute('aria-current') === 'true');
    return current === -1 ? 0 : current;
  }

  /** Bound via `on:click="/previousBump"` — survives cart morphs. */
  previousBump() {
    this._interact(-1);
  }

  /** Bound via `on:click="/nextBump"` — survives cart morphs. */
  nextBump() {
    this._interact(1);
  }

  /**
   * @param {number} delta - The direction to move the carousel.
   */
  _interact(delta) {
    this._stop();
    this._goTo(this._index + delta);
    if (this._autoScroll) this._start();
  }

  /**
   * Dismisses the order bumps carousel for the rest of the browsing session.
   */
  handleClose() {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    this._stop();
    this.hidden = true;
  }

  _goTo(index) {
    const track = this.querySelector('.order-bumps__track');
    if (!track) return;

    const next = (index + this._count) % this._count;
    track.style.transform = `translateX(calc(-${next} * 100%))`;
    this.querySelectorAll('.order-bumps__dot').forEach((dot, i) => {
      dot.setAttribute('aria-current', i === next ? 'true' : 'false');
    });
  }

  _next() {
    this._goTo(this._index + 1);
  }

  _start() {
    this._stop();
    this._timer = setInterval(() => this._next(), this._delay);
  }

  _stop() {
    clearInterval(this._timer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stop();
    this._controller?.abort();
    this._observer?.disconnect();
  }
}

if (!customElements.get('order-bumps-carousel')) {
  customElements.define('order-bumps-carousel', OrderBumpsCarousel);
}
